import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../api';

const SessionDetail = ({ sessionId, onBack, onCompare, hideHeader = false }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFullTranscript, setShowFullTranscript] = useState(false);
  const [transcript, setTranscript] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    topics: false,
    outcomes: false,
    challenges: false,
    engagement: false,
    worked_well: false,
    next_steps: false
  });
  const [isOverviewExpanded, setIsOverviewExpanded] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const loadSessionDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiFetch(`/api/sessions/${sessionId}`);

      if (!response.ok) {
        throw new Error(`Failed to load session: ${response.status}`);
      }

      const data = await response.json();
      setSession(data);
      setError(null);
    } catch (err) {
      console.error('Error loading session detail:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadSessionDetail();
  }, [loadSessionDetail]);

  const loadFullTranscript = async () => {
    if (transcript) {
      setShowFullTranscript(true);
      return;
    }

    try {
      const response = await apiFetch(`/api/sessions/${sessionId}/transcript`);

      if (!response.ok) {
        throw new Error('Failed to load transcript');
      }

      const data = await response.json();
      setTranscript(data.transcript);
      setShowFullTranscript(true);
    } catch (err) {
      console.error('Error loading transcript:', err);
      alert('Failed to load transcript: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    const match = sessionId.match(/audio(\d{4})/);
    if (match) {
      const dateCode = match[1];
      const month = dateCode.substring(0, 2);
      const day = dateCode.substring(2, 4);
      return `${month}/${day}/2024`;
    }
    return dateString.substring(0, 10);
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div className="lrbrain-typing-indicator">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <p style={{ marginTop: '1rem', color: 'var(--lrbrain-text)', opacity: 0.7 }}>
          Loading session details...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: 'var(--lrbrain-error)',
        background: 'rgba(239, 68, 68, 0.1)',
        borderRadius: '12px',
        margin: '1rem'
      }}>
        <p>❌ {error}</p>
        <button
          onClick={onBack}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            background: 'var(--lrbrain-accent)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          ← Back to Sessions
        </button>
      </div>
    );
  }

  if (!session) return null;

  const summary = session.summary || {};

  return (
    <div style={{
      padding: '0.75rem',
      maxWidth: '1000px',
      margin: '0 auto'
    }}>
      {/* Compact header bar with date, back and compare */}
      {!hideHeader && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.75rem',
          gap: '0.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button
              onClick={onBack}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '0.25rem 0.5rem',
                fontSize: '0.8125rem',
                color: 'var(--lrbrain-text-light)',
                cursor: 'pointer',
                fontFamily: 'var(--lrbrain-font-main)'
              }}
            >
              ← Back
            </button>
            <span style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'var(--lrbrain-navy)'
            }}>
              📚 {formatDate(session.id)}
            </span>
          </div>
          <button
            onClick={() => onCompare && onCompare(sessionId)}
            style={{
              background: 'var(--lrbrain-accent)',
              border: 'none',
              borderRadius: '6px',
              padding: '0.375rem 0.75rem',
              fontSize: '0.8125rem',
              color: 'white',
              cursor: 'pointer',
              fontFamily: 'var(--lrbrain-font-main)',
              fontWeight: 500
            }}
          >
            Compare
          </button>
        </div>
      )}

      {/* Compact session overview - expandable */}
      {summary.session_overview && (
        <div
          onClick={() => setIsOverviewExpanded(!isOverviewExpanded)}
          style={{
            background: 'linear-gradient(135deg, var(--lrbrain-accent), var(--lrbrain-navy))',
            borderRadius: '10px',
            padding: '0.75rem 1rem',
            marginBottom: '0.75rem',
            color: 'white',
            cursor: 'pointer',
            transition: 'all 200ms ease'
          }}
        >
          <p style={{
            fontSize: '0.875rem',
            lineHeight: '1.5',
            margin: 0,
            opacity: 0.95,
            ...(!isOverviewExpanded && {
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            })
          }}>
            {summary.session_overview}
          </p>
          <button
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '4px',
              padding: '2px 8px',
              marginTop: '0.5rem',
              fontSize: '0.75rem',
              color: 'white',
              cursor: 'pointer',
              opacity: 0.9
            }}
          >
            {isOverviewExpanded ? '▲ Show less' : '▼ Show more'}
          </button>
        </div>
      )}

      {/* Accordion Summary Sections */}
      <div className="session-summary-accordions">
        {summary.topics_covered && summary.topics_covered.length > 0 && (
          <AccordionSection
            id="topics"
            title="Topics Covered"
            icon="📝"
            color="var(--lrbrain-accent)"
            items={summary.topics_covered}
            isExpanded={expandedSections.topics}
            onToggle={() => toggleSection('topics')}
          />
        )}

        {summary.learning_outcomes && summary.learning_outcomes.length > 0 && (
          <AccordionSection
            id="outcomes"
            title="Learning Outcomes"
            icon="✅"
            color="var(--lrbrain-success)"
            items={summary.learning_outcomes}
            isExpanded={expandedSections.outcomes}
            onToggle={() => toggleSection('outcomes')}
          />
        )}

        {summary.challenges_identified && summary.challenges_identified.length > 0 && (
          <AccordionSection
            id="challenges"
            title="Challenges Identified"
            icon="⚠️"
            color="var(--lrbrain-warning)"
            items={summary.challenges_identified}
            isExpanded={expandedSections.challenges}
            onToggle={() => toggleSection('challenges')}
          />
        )}

        {summary.student_engagement && summary.student_engagement.length > 0 && (
          <AccordionSection
            id="engagement"
            title="Student Engagement"
            icon="💬"
            color="var(--lrbrain-purple)"
            items={summary.student_engagement}
            isExpanded={expandedSections.engagement}
            onToggle={() => toggleSection('engagement')}
          />
        )}

        {summary.what_worked_well && summary.what_worked_well.length > 0 && (
          <AccordionSection
            id="worked_well"
            title="What Worked Well"
            icon="⭐"
            color="var(--lrbrain-accent)"
            items={summary.what_worked_well}
            isExpanded={expandedSections.worked_well}
            onToggle={() => toggleSection('worked_well')}
          />
        )}

        {summary.next_steps && summary.next_steps.length > 0 && (
          <AccordionSection
            id="next_steps"
            title="Next Steps"
            icon="🎯"
            color="var(--lrbrain-navy)"
            items={summary.next_steps}
            isExpanded={expandedSections.next_steps}
            onToggle={() => toggleSection('next_steps')}
          />
        )}
      </div>

      {/* Transcript section */}
      {session.has_transcript && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          border: '2px solid var(--lrbrain-border)',
          padding: '1.5rem',
          marginTop: '2rem'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <h3 style={{
              fontFamily: 'var(--lrbrain-font-fun)',
              color: 'var(--lrbrain-navy)',
              margin: 0
            }}>
              📄 Transcript
            </h3>
            {!showFullTranscript && (
              <button
                onClick={loadFullTranscript}
                style={{
                  background: 'var(--lrbrain-accent)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  fontFamily: 'var(--lrbrain-font-main)'
                }}
              >
                View Full Transcript
              </button>
            )}
          </div>

          {showFullTranscript && transcript ? (
            <div style={{
              maxHeight: '400px',
              overflowY: 'auto',
              background: 'var(--lrbrain-bg)',
              padding: '1rem',
              borderRadius: '8px',
              fontSize: '0.875rem',
              lineHeight: '1.6',
              color: 'var(--lrbrain-text)',
              whiteSpace: 'pre-wrap'
            }}>
              {transcript.scrubbed_transcript || transcript.transcript || transcript.text}
            </div>
          ) : (
            <p style={{
              fontSize: '0.875rem',
              lineHeight: '1.6',
              color: 'var(--lrbrain-text)',
              margin: 0
            }}>
              {session.transcript_preview}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Accordion section component for collapsible summary sections
const AccordionSection = ({ id, title, icon, color, items = [], isExpanded, onToggle }) => {
  const itemCount = items.length;
  const preview = items.length > 0 ? items[0].substring(0, 50) + (items[0].length > 50 ? '...' : '') : '';

  return (
    <div className={`session-accordion ${isExpanded ? 'session-accordion--expanded' : ''}`} style={{ '--accent-color': color }}>
      <button
        className="session-accordion__header"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <div className="session-accordion__header-left">
          <span className="session-accordion__icon">{icon}</span>
          <span className="session-accordion__title">{title}</span>
          <span className="session-accordion__count">{itemCount}</span>
        </div>
        <div className="session-accordion__header-right">
          {!isExpanded && itemCount > 0 && (
            <span className="session-accordion__preview">{preview}</span>
          )}
          <svg
            className="session-accordion__chevron"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points={isExpanded ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
          </svg>
        </div>
      </button>

      <div className={`session-accordion__content ${isExpanded ? 'session-accordion__content--open' : ''}`}>
        <ul className="session-accordion__list">
          {items.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default SessionDetail;
